import React from 'react';
import { ReactComponent as CloseIcon } from '$/images/close.svg';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import { CancelReason, useCancelSubscriptionMutation } from '$/GraphQL/schema/schema';
import { logger } from '$/utils/logger';
import { css } from '@emotion/react';
import { ApolloError } from '@apollo/client';
import EDate from '$/shared/frontend/utils/EDate';
import Modal from '$/components/ui/Modal';
import Text from '$/components/ui/Text';
import Button from '$/components/ui/Button';

interface TeamSuspendedModalProps {
  nextPaymentDueDate: EDate;
  toggleModal: () => void;
  cancelReasonOption?: CancelReason;
}

export const CancelSubscriptionModal: React.FC<TeamSuspendedModalProps> = ({
  toggleModal,
  nextPaymentDueDate,
  cancelReasonOption,
}) => {
  const [cancelSubscriptionMutation, cancelSubscriptionMutationResult] =
    useCancelSubscriptionMutation();
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const handleCancelSubscription = async (): Promise<void> => {
    if (!cancelReasonOption) {
      return;
    }
    try {
      setErrorMessage('');
      await cancelSubscriptionMutation({
        variables: {
          data: cancelReasonOption,
        },
        update: (_, result) => {
          if (result.data?.cancelSubscription?.id) {
            // delay a little bit for the webhook done
            setTimeout(() => window.location.reload(), 500);
          }
        },
      });
    } catch (e) {
      const error = e as ApolloError;

      logger.error('Cancel subscription error', { error });

      setErrorMessage(
        error.graphQLErrors && error.graphQLErrors.length > 0
          ? error.graphQLErrors[0].message
          : error.message
      );
    }
  };
  return (
    <Modal closeOnClickOutside widthClass='container mx-auto max-w-35e' onClose={toggleModal}>
      <Modal.StickyButtonHeader>
        <Modal.Close onClick={toggleModal} />
      </Modal.StickyButtonHeader>
      <Modal.Body>
        <div className='flex justify-center'>
          <ErrorIcon>
            <CloseIcon />
          </ErrorIcon>
        </div>
        <Text.subtitle className='my-0 mt-1/2e w-10/12 mx-auto text-center'>
          Cancel my subscription
        </Text.subtitle>
        <div
          className='py-1e'
          css={css`
            div,
            p {
              margin-top: 1.25rem;
            }
          `}
        >
          <Text.body className='text-normal'>
            If you cancel, all of your eWebinars will be unpublished on{' '}
            <span className='font-semibold'>{nextPaymentDueDate.format('P')}</span>, which means:
          </Text.body>
          <ul>
            <li className='mb-1/2e'>
              You will no longer have access to analytics, registrant data, or chat history for any
              of your eWebinars.
            </li>
            <li className='mb-1/2e'>
              Your registrants will no longer have access to replays or any upcoming sessions.
            </li>
            <li className='text-red-1 font-bold'>
              30 days after cancelation without renewal, all of your eWebinars and their associated
              data will be deleted permanently.
            </li>
          </ul>
          <Text.body className='text-normal'>
            For these reasons, we recommend you download your registrant lists before canceling, if
            necessary.
          </Text.body>
          <Text.body className='text-normal'>
            Would you still like to cancel your subscription?
          </Text.body>
          {errorMessage && <div className='text-coral-1'>{errorMessage}</div>}
        </div>
      </Modal.Body>
      <Modal.Footer className='flex items-center justify-between'>
        <Button className='text-gray-1' onClick={toggleModal}>
          Continue subscription
        </Button>
        <Button.submit
          className='bg-color-delete'
          submitting={cancelSubscriptionMutationResult.loading}
          onClick={handleCancelSubscription}
        >
          Cancel my subscription
        </Button.submit>
      </Modal.Footer>
    </Modal>
  );
};

const ErrorIcon = styled.div`
  background: #f9e3e2;
  ${tw`p-4 rounded-full`}
  svg {
    width: 25px;
    height: 25px;
    path {
      fill: #e2756e;
    }
  }
`;
